// src/pages/CRM/Core/components/ContactGroupManager.js
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
    TextField, Box, Typography, CircularProgress, Alert
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import axiosInstance from '../../../../../utils/axiosInstance';

const ContactGroupManager = ({ open, onClose, onChange }) => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Form state
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '' });

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/crm/contact-groups');
            if (res.data.success) setGroups(res.data.data);
        } catch (err) { setError('Failed to load groups'); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (open) fetchGroups(); }, [open]);

    const handleSubmit = async () => {
        if (!formData.name.trim()) return;
        try {
            if (editingId) {
                await axiosInstance.put(`/api/crm/contact-groups/${editingId}`, formData);
            } else {
                await axiosInstance.post('/api/crm/contact-groups', formData);
            }
            setFormData({ name: '', description: '' });
            setEditingId(null);
            fetchGroups();
            if (onChange) onChange();
        } catch (err) { setError('Operation failed'); }
    };

    const handleEdit = (group) => {
        setEditingId(group.id);
        setFormData({ name: group.name, description: group.description || '' });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this group?')) return;
        try {
            await axiosInstance.delete(`/api/crm/contact-groups/${id}`);
            fetchGroups();
            if (onChange) onChange();
        } catch (err) { setError('Failed to delete'); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Manage Contact Groups</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                
                {/* Add/Edit Form */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <TextField 
                        label="Group Name" size="small" fullWidth 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 0.5 }}
                        onClick={handleSubmit}
                        startIcon={editingId ? <SaveIcon /> : <AddIcon />}
                    >
                        {editingId ? 'Save' : 'Add'}
                    </Button>
                    {editingId && (
                        <IconButton onClick={() => { setEditingId(null); setFormData({name:'', description:''}); }}>
                            <CancelIcon />
                        </IconButton>
                    )}
                </Box>

                {/* List */}
                {loading ? <CircularProgress /> : (
                    <List>
                        {groups.map(group => (
                            <ListItem key={group.id} divider>
                                <ListItemText 
                                    primary={group.name} 
                                    secondary={group.contact_count + ' contacts'} 
                                />
                                <ListItemSecondaryAction>
                                    <IconButton size="small" onClick={() => handleEdit(group)}><EditIcon /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(group.id)}><DeleteIcon /></IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {groups.length === 0 && <Typography color="textSecondary" align="center">No groups found</Typography>}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
export default ContactGroupManager;